import React, { useMemo } from 'react';
import { Calendar, AlertCircle, Link2, UserCheck } from 'lucide-react';
import { useT } from '../utils/translate';

// amoCRM'dagi Vazifalar bo'limining boshlang'ich (standart) ko'rinishi —
// bosqich emas, MUDDAT bo'yicha ustunlarga taqsimlangan kanban: Muddati o'tgan /
// Bugun / Ertaga / Bu hafta (+ ichida Keyingi hafta) / Sanasiz. Sof o'qish uchun
// (drag yo'q — ustunlar tahrirlanadigan bosqich emas, hisoblangan sana oralig'i).
const pad = (n) => String(n).padStart(2, '0');
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const mondayOffset = (d) => (d.getDay() + 6) % 7; // Mon=0

function isOverdue(dueDate) {
  return !!dueDate && new Date(dueDate) < new Date();
}

function fmtWhen(dueDate) {
  const d = new Date(dueDate);
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${time}`;
}

function DueTaskCard({ task, onView }) {
  const overdue = isOverdue(task.dueDate);
  return (
    <button onClick={() => onView(task)}
      className={`w-full text-left bg-white border rounded-xl p-2.5 shadow-sm transition-all hover:shadow-md ${
        overdue ? 'border-red-200 bg-red-50/30' : 'border-surface-200 hover:border-surface-300'
      }`}>
      <p className="text-sm font-medium text-ink leading-snug truncate">{task.title}</p>
      {task.contact?.name && (
        <div className="flex items-center gap-1 mt-1">
          <Link2 className="w-3 h-3 text-ink-disabled shrink-0" />
          <span className="text-[11px] text-ink-tertiary truncate">{task.contact.name}</span>
        </div>
      )}
      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
        {task.dueDate && (
          <span className={`flex items-center gap-1 text-[10px] font-medium ${overdue ? 'text-red-500' : 'text-ink-tertiary'}`}>
            {overdue && <AlertCircle className="w-3 h-3" />}
            <Calendar className="w-3 h-3" />
            {fmtWhen(task.dueDate)}
          </span>
        )}
        {task.assignedTo?.name && (
          <span className="flex items-center gap-1 text-[10px] text-ink-tertiary ml-auto">
            <UserCheck className="w-3 h-3" /> {task.assignedTo.name}
          </span>
        )}
      </div>
    </button>
  );
}

function DueColumn({ title, count, accent, tasks, onView }) {
  return (
    // min-h-0: flex-item'ning standart min-height:auto xossasi ustunni siqilishga
    // qo'ymay, ichidagi overflow-y-auto'ni ishlamay qoladi — shu sabab scroll chiqmasdi.
    <div className="flex flex-col flex-1 min-w-64 shrink-0 min-h-0">
      <div className="mb-2 px-1 shrink-0">
        <div className={`h-0.5 rounded-full mb-2 ${accent || 'bg-surface-200'}`} />
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-ink-tertiary uppercase tracking-wide">{title}</span>
          <span className="text-xs text-ink-disabled bg-surface-100 rounded-full px-1.5 py-0.5">{count}</span>
        </div>
      </div>
      <div className="flex-1 min-h-0 rounded-xl p-2 space-y-2 overflow-y-auto bg-surface-100">
        {tasks.map(task => <DueTaskCard key={task._id} task={task} onView={onView} />)}
        {tasks.length === 0 && <p className="text-xs text-ink-disabled text-center py-6">—</p>}
      </div>
    </div>
  );
}

export default function TasksDueBoard({ tasks, onView }) {
  const t = useT();

  const buckets = useMemo(() => {
    const now = new Date();
    const todayStart = startOfDay(now);
    const tomorrowStart = new Date(todayStart); tomorrowStart.setDate(todayStart.getDate() + 1);
    const dayAfterStart  = new Date(todayStart); dayAfterStart.setDate(todayStart.getDate() + 2);
    const weekEnd = new Date(todayStart); weekEnd.setDate(todayStart.getDate() + (6 - mondayOffset(todayStart)) + 1); // keyingi dushanba 00:00

    const out = { overdue: [], today: [], tomorrow: [], thisWeek: [], nextWeek: [], noDate: [] };
    for (const task of tasks) {
      if (!task.dueDate) { out.noDate.push(task); continue; }
      const due = new Date(task.dueDate);
      if (due < now)                out.overdue.push(task);
      else if (due < tomorrowStart) out.today.push(task);
      else if (due < dayAfterStart) out.tomorrow.push(task);
      else if (due < weekEnd)       out.thisWeek.push(task);
      else                          out.nextWeek.push(task); // keyingi hafta va undan uzoqroq — bitta ustunda
    }
    for (const key of Object.keys(out)) out[key].sort((a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0));
    return out;
  }, [tasks]);

  return (
    <div className="flex gap-4 h-full items-stretch">
      <DueColumn title={t('tasks.colOverdue')}   count={buckets.overdue.length}   accent="bg-red-400"     tasks={buckets.overdue}   onView={onView} />
      <DueColumn title={t('tasks.colToday')}     count={buckets.today.length}     accent="bg-emerald-400" tasks={buckets.today}     onView={onView} />
      <DueColumn title={t('tasks.colTomorrow')}  count={buckets.tomorrow.length}  accent="bg-primary-400" tasks={buckets.tomorrow}  onView={onView} />
      <div className="flex flex-col flex-1 min-w-64 shrink-0 min-h-0 gap-4">
        <DueColumn title={t('tasks.colThisWeek')} count={buckets.thisWeek.length} accent="bg-amber-400" tasks={buckets.thisWeek} onView={onView} />
        {buckets.nextWeek.length > 0 && (
          <DueColumn title={t('tasks.colNextWeek')} count={buckets.nextWeek.length} tasks={buckets.nextWeek} onView={onView} />
        )}
        {buckets.noDate.length > 0 && (
          <DueColumn title={t('tasks.noDateBucket')} count={buckets.noDate.length} tasks={buckets.noDate} onView={onView} />
        )}
      </div>
    </div>
  );
}
